import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api.js'
import { copyText } from '../../utils/clipboard.js'
import { formatDateTime } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

export default function RegisterComplaints() {
  const { t } = useTranslation('ops')
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'

  const [link, setLink] = useState(null)
  const [lastSent, setLastSent] = useState(null)   // { email, chat }
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [complaints, setComplaints] = useState([])
  const [requested, setRequested] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [msg, setMsg] = useState(null)

  // Admin: recipient + delivery options
  const [users, setUsers] = useState([])
  const [recipientId, setRecipientId] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [sendChat, setSendChat] = useState(true)

  useEffect(() => {
    if (isAdmin) {
      api.get('/legal/complaints').then(r => setComplaints(r.data || [])).catch(() => {})
      api.get('/users/').then(r => setUsers(r.data || [])).catch(() => {})
    }
  }, [isAdmin])

  const generate = async () => {
    setGenerating(true); setMsg(null); setLastSent(null)
    try {
      const recipient = users.find(u => String(u.id) === String(recipientId))
      const body = {
        send_email: sendEmail && !!recipient,
        send_chat: sendChat && !!recipient,
      }
      if (recipient) { body.target_user_id = recipient.id; body.target_email = recipient.email }
      const r = await api.post('/legal/complaint-invites', body)
      setLink(r.data.url)
      setLastSent({ email: r.data.sent_email, chat: r.data.sent_chat, name: recipient?.full_name })
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not generate link' })
    } finally { setGenerating(false) }
  }

  const copy = async () => {
    const ok = await copyText(link)
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000) }
    else setMsg({ type: 'error', text: 'Could not copy — select the link and copy manually.' })
  }

  const requestLink = async () => {
    setRequesting(true); setMsg(null)
    try {
      await api.post('/legal/complaints/request-link')
      setRequested(true)
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Could not send request' })
    } finally { setRequesting(false) }
  }

  const downloadAttachment = async (complaintId, att) => {
    try {
      const resp = await api.get(`/legal/complaints/${complaintId}/attachments/${att.id}`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url; a.download = att.filename; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } catch {
      setMsg({ type: 'error', text: 'Could not download the file.' })
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">{t('register_complaints')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isAdmin
            ? 'Generate a secure complaint form link, email/chat it to the user, and review complaints received.'
            : 'To register a complaint, request a secure form link from the Admin.'}
        </p>
      </div>

      {msg && <div className={`text-sm px-3 py-2 rounded-lg ${msg.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>{msg.text}</div>}

      {/* Non-admin: request a link */}
      {!isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          {requested ? (
            <div className="text-center py-4">
              <div className="text-4xl mb-2">📨</div>
              <p className="text-sm text-gray-700">Your request was sent to the Admin. They'll share a complaint form link with you (by email / chat / notification).</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">Complaints are filed through a secure link issued by the Admin. Click below to request one.</p>
              <button onClick={requestLink} disabled={requesting} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {requesting ? 'Sending…' : 'Request a Complaint Link'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Admin: generate + send link */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-800 mb-3">{t('generate_link')}</h3>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('send_to_user')}</label>
              <select value={recipientId} onChange={e => setRecipientId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Just generate a link (I'll share it) —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role}) · {u.email}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 pb-1">
              <label className={`flex items-center gap-1.5 text-sm ${recipientId ? 'text-gray-700' : 'text-gray-400'}`}>
                <input type="checkbox" checked={sendEmail} disabled={!recipientId} onChange={e => setSendEmail(e.target.checked)} /> Email
              </label>
              <label className={`flex items-center gap-1.5 text-sm ${recipientId ? 'text-gray-700' : 'text-gray-400'}`}>
                <input type="checkbox" checked={sendChat} disabled={!recipientId} onChange={e => setSendChat(e.target.checked)} /> Chat
              </label>
            </div>
          </div>
          <button onClick={generate} disabled={generating} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {generating ? 'Working…' : (recipientId ? '➤ Generate & Send' : '➕ Generate Link')}
          </button>

          {link && (
            <>
              <div className="mt-4 flex gap-2 items-center">
                <input readOnly value={link} onClick={e => e.target.select()} className="flex-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700" />
                <button onClick={copy} className={`px-3 py-2 text-xs font-medium rounded-lg ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-700 text-white hover:bg-gray-800'}`}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {lastSent && (lastSent.email || lastSent.chat) && (
                <p className="text-xs text-green-700 mt-2">
                  Sent to {lastSent.name}: {[lastSent.email && 'email', lastSent.chat && 'chat'].filter(Boolean).join(' + ')}.
                </p>
              )}
              <p className="text-xs text-gray-400 mt-2">{t('single_use_link')}</p>
            </>
          )}
        </div>
      )}

      {/* Admin: complaints received */}
      {isAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-5 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">{t('complaints_received')}</h3>
          </div>
          {complaints.length === 0 ? (
            <p className="p-6 text-sm text-gray-400 text-center">{t('no_complaints')}</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {complaints.map(c => (
                <div key={c.id} className="p-5">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 rounded">{c.complaint_number}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{c.status}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{c.name}{c.organization ? ` — ${c.organization}` : ''}</p>
                  <p className="text-xs text-gray-500">{c.email} · {formatDateTime(c.created_at)}</p>
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{c.message}</p>
                  {c.attachments && c.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.attachments.map(att => (
                        <button key={att.id} onClick={() => downloadAttachment(c.id, att)}
                          className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100">
                          📎 {att.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
